import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Camera, CheckCircle2, ImagePlus, Loader2, MapPin, ExternalLink, Video } from "lucide-react";
import { SitePlan } from "../../components/workflow/SitePlan";
import { api } from "../../lib/api";
import { compressImage } from "../../lib/compressImage";
import { uploadVideo, videoDuration } from "../../lib/videoUpload";
import { useStaff } from "./StaffLayout";

const todayStr = () => new Intl.DateTimeFormat("sv-SE", { timeZone: "Asia/Tokyo" }).format(new Date());
const imgUrl = (id) => `${api.defaults.baseURL}/site-photos/${id}/image`;
const inputCls = "w-full border border-line bg-ink px-3 py-3 text-sm text-white outline-none focus:border-accent";

const ShotCard = ({ index, shot, photos, busy, progress, onShoot, t }) => {
  const done = photos.some((p) => p.status !== "差戻し" && p.state !== "failed");
  const failed = photos.find((p) => p.state === "failed");
  return (
    <button data-testid={`staff-shot-${index}`} onClick={onShoot} disabled={busy} className={`flex w-full gap-3 border p-3 text-left transition-colors active:scale-[0.99] ${done ? "border-emerald-500/50 bg-emerald-500/5" : "border-line bg-coal hover:border-accent"}`}>
      <span className="flex h-12 w-12 shrink-0 items-center justify-center bg-ink text-accent">
        {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : done ? <CheckCircle2 className="h-6 w-6 text-emerald-400" /> : shot.media === "video" ? <Video className="h-6 w-6" /> : <Camera className="h-6 w-6" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs text-accent">{String(index + 1).padStart(2, "0")}</span>
          <span className="text-sm font-bold text-white">{shot.title}</span>
          {shot.required && <span className="border border-accent/50 px-1.5 text-[10px] text-accent">{t("shot_required")}</span>}
        </span>
        {shot.instruction && <span className="mt-1 block whitespace-pre-wrap text-xs leading-relaxed text-gray-300">{shot.instruction}</span>}
        <span data-testid={`staff-shot-status-${index}`} className={`mt-2 block text-[11px] font-bold ${done ? "text-emerald-400" : "text-gray-500"}`}>
          {busy ? `${t("uploading")}${shot.media === "video" ? ` ${progress}%` : ""}` : done ? `${t("shot_taken")}（${photos.length}）・${t("shot_again")}` : shot.media === "video" ? t("video_tap") : t("shot_tap")}
        </span>
        {failed && !done && <span data-testid={`staff-shot-failed-${index}`} className="mt-1 block text-[11px] text-red-400">{failed.error || t("upload_failed")}</span>}
        {photos.length > 0 && (
          <span className="mt-2 flex flex-wrap gap-1.5">
            {photos.map((p) => (p.kind === "video" ? <span key={p.id} className="flex h-12 w-12 items-center justify-center bg-ink text-accent"><Video className="h-5 w-5" /></span> : <img key={p.id} src={imgUrl(p.id)} alt="" className={`h-12 w-12 object-cover ${p.status === "差戻し" ? "opacity-40" : ""}`} />))}
          </span>
        )}
      </span>
    </button>
  );
};

const takenOf = (photos, id) => photos.filter((p) => p.guide_id === id);
const isDone = (photos, id) => takenOf(photos, id).some((p) => p.status !== "差戻し" && p.state !== "failed");

const SurveyForm = ({ project, missing, onSaved, t }) => {
  const [date, setDate] = useState(project.survey?.date || todayStr());
  const [note, setNote] = useState(project.survey?.note || "");
  const [state, setState] = useState({ saving: false, msg: "", ok: false });
  const submit = async () => {
    setState({ saving: true, msg: "", ok: false });
    try {
      await api.put(`/staff/surveys/${project.id}`, { date, note });
      toast.success(t("survey_saved"));
      onSaved();
    } catch (err) {
      setState({ saving: false, msg: typeof err.response?.data?.detail === "string" ? err.response.data.detail : t("upload_failed"), ok: false });
    }
  };
  return (
    <section data-testid="staff-survey-form" className="space-y-3 border border-line bg-coal p-4">
      <h3 className="font-mincho text-base font-bold text-white">{t("survey_result")}</h3>
      {project.survey?.by_name && <p data-testid="staff-survey-recorded" className="text-xs text-emerald-400">{t("survey_recorded")}：{project.survey.by_name}（{project.survey.date}）</p>}
      <input data-testid="staff-survey-date" type="date" max={todayStr()} value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
      <textarea data-testid="staff-survey-note" rows={5} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("survey_note_ph")} className={inputCls} />
      {missing.length > 0 && <p data-testid="staff-survey-missing" className="text-xs text-amber-300">{t("survey_missing")}：{missing.map((n) => `No.${n}`).join(" ")}</p>}
      {state.msg && <p data-testid="staff-survey-message" className={`text-sm ${state.ok ? "text-emerald-400" : "text-red-400"}`}>{state.msg}</p>}
      <button data-testid="staff-survey-submit" onClick={submit} disabled={state.saving || !note.trim() || missing.length > 0} className="w-full bg-accent py-4 text-sm font-bold text-ink transition-colors hover:bg-accent-hover disabled:opacity-40">
        {state.saving ? "..." : t("survey_submit")}
      </button>
    </section>
  );
};

export default function StaffSurvey() {
  const { t } = useStaff();
  const [projects, setProjects] = useState(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState(params.get("project") || "");
  const [shooting, setShooting] = useState(null);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const [progress, setProgress] = useState(0);

  const load = () => api.get("/staff/surveys").then((r) => {
    setProjects(r.data);
    setProjectId((cur) => (r.data.some((p) => p.id === cur) ? cur : "") || r.data.find((p) => p.photo_guide.length)?.id || r.data[0]?.id || "");
  }).catch(() => setProjects([]));
  useEffect(() => { load(); }, []);

  const project = projects?.find((p) => p.id === projectId);
  const shoot = (guideId) => {
    const ref = project?.photo_guide.find((g) => g.id === guideId)?.media === "video" ? videoRef : fileRef;
    setShooting(guideId); setError("");
    if (ref.current) { ref.current.value = ""; ref.current.click(); }
  };

  const uploadMovie = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !project || !shooting) return;
    const duration = await videoDuration(file);
    if (duration > 61) { setError(t("video_too_long")); return; }
    setBusyId(shooting); setProgress(0);
    try {
      await uploadVideo(file, { projectId: project.id, guideId: shooting, duration, onProgress: setProgress });
      await load();
      setTimeout(load, 8000);
    } catch {
      setError(t("upload_failed"));
    } finally {
      setBusyId("");
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !project) return;
    const guide = project.photo_guide.find((g) => g.id === shooting);
    setBusyId(shooting || "extra");
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(file));
      fd.append("project_id", project.id);
      fd.append("site_id", project.sites[0]?.id || "");
      fd.append("category", "施工前");
      fd.append("guide_id", guide?.id || "");
      fd.append("title", guide ? guide.title : t("extra_photo"));
      fd.append("description", "現地調査");
      await api.post("/staff/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch {
      setError(t("upload_failed"));
    } finally {
      setBusyId("");
    }
  };

  if (projects === null) return <p className="px-4 py-6 text-sm text-gray-500">...</p>;
  const guide = project?.photo_guide || [];
  const onPlan = !!project?.photo_plan && project.photo_plan.kind !== "none" && guide.some((g) => g.x != null);
  const missing = guide.map((g, i) => ({ g, n: i + 1 })).filter(({ g }) => g.required && !isDone(project.photos, g.id)).map(({ n }) => `${n}`);

  return (
    <div data-testid="staff-survey-page" className="mx-auto max-w-lg space-y-6 px-4 py-6">
      <h2 className="font-mincho text-lg font-bold text-white">{t("survey_title")}</h2>
      {projects.length === 0 ? <p data-testid="staff-survey-none" className="text-sm text-gray-500">{t("survey_none")}</p> : (
        <>
          <select data-testid="staff-survey-project" value={projectId} onChange={(e) => setProjectId(e.target.value)} className={inputCls}>
            {projects.map((p) => <option key={p.id} value={p.id}>[{p.code || "-"}] {p.name}</option>)}
          </select>
          {project?.address && <a data-testid="staff-survey-address" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(project.address)}`} target="_blank" rel="noreferrer" className="flex items-start gap-2 text-sm text-accent underline underline-offset-4 active:opacity-70"><MapPin className="mt-0.5 h-4 w-4 shrink-0" />{project.address}<ExternalLink className="mt-1 h-3 w-3 shrink-0" /></a>}
          {project?.description && <p className="whitespace-pre-wrap border-l-2 border-line pl-3 text-xs text-gray-400">{project.description}</p>}
          <section className="space-y-3">
            <h3 className="text-sm font-bold text-white">{t("shot_guide")}</h3>
            {onPlan && (
              <div data-testid="staff-plan" className="space-y-1">
                <div className="relative">
                  <SitePlan plan={project.photo_plan} items={guide} colorFor={(g) => (busyId === g.id ? "#facc15" : isDone(project.photos, g.id) ? "#34d399" : "#ff6a1a")} onSelect={(i) => !busyId && shoot(guide[i].id)} />
                  {busyId && busyId !== "extra" && <div data-testid="staff-plan-uploading" className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 bg-ink/80 py-2 text-xs text-amber-300"><Loader2 className="h-4 w-4 animate-spin" />{t("uploading")}{guide.find((g) => g.id === busyId)?.media === "video" ? ` ${progress}%` : ""}</div>}
                </div>
                <p className="text-[11px] text-gray-500">{t("plan_hint")}</p>
              </div>
            )}
            {guide.length === 0 && <p data-testid="staff-shot-empty" className="text-xs text-gray-500">{t("shot_none")}</p>}
            {guide.map((g, i) => (onPlan && g.x != null ? null : <ShotCard key={g.id} index={i} shot={g} t={t} busy={busyId === g.id} progress={progress} photos={project.photos.filter((p) => p.guide_id === g.id)} onShoot={() => shoot(g.id)} />))}
            <button data-testid="staff-shot-extra" onClick={() => shoot(null)} disabled={!!busyId} className="flex w-full items-center justify-center gap-2 border border-dashed border-line py-4 text-xs text-gray-400 transition-colors hover:border-accent hover:text-accent">
              {busyId === "extra" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}{t("extra_photo")}
            </button>
            <input ref={videoRef} data-testid="staff-shot-video-file" type="file" accept="video/*" capture="environment" className="hidden" onChange={uploadMovie} />
            <input ref={fileRef} data-testid="staff-shot-file" type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} />
            {error && <p data-testid="staff-shot-error" className="text-sm text-red-400">{error}</p>}
          </section>
          {project && <SurveyForm key={project.id} project={project} missing={missing} onSaved={() => navigate("/staff")} t={t} />}
        </>
      )}
    </div>
  );
}
